import { useState, type FormEvent } from 'react';

interface SubmitResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: string };
}

export default function SubscribeSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const trap = String(formData.get('website_alt') || '').trim();

    if (trap) {
      setStatus('success');
      setMessage('订阅成功，档期换挡时我们会第一时间提醒你。');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: SubmitResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as SubmitResponse;
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        setMessage('订阅成功，档期换挡时我们会第一时间提醒你。');
        form.reset();
      } else {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试，或通过页脚邮箱联系我们。');
      }
    } catch {
      setStatus('error');
      setMessage('网络异常，提交没有成功，请稍后重试。');
    }
  };

  return (
    <section id="subscribe" className="py-14 md:py-20 bg-background-50">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="relative rounded-lg overflow-hidden bg-primary-500">
          <div className="grid grid-cols-1 lg:grid-cols-2">
            <div className="p-7 md:p-10 lg:p-12">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-background-50/20 text-background-50 text-xs">
                <i className="ri-notification-3-line" aria-hidden="true"></i>
                档期提醒
              </span>
              <h2 className="mt-5 font-heading text-2xl md:text-3xl font-bold text-background-50 leading-tight">
                订阅档期更新
                <br />
                换挡的时候，喊你一声
              </h2>
              <p className="mt-4 text-sm md:text-base text-primary-50 leading-7 max-w-md">
                我们只在上新与档期换挡时发送提醒，不会频繁打扰。订阅后你会优先收到「今日上新」与「周末限定」的更新通知。
              </p>
              <ul className="mt-6 space-y-2.5">
                {['每日更新提醒，不超过两天一封', '周末与假日档期提前预告', '随时可退订，邮件一键操作'].map((t) => (
                  <li key={t} className="flex items-center gap-2.5 text-sm text-primary-50">
                    <i className="ri-checkbox-circle-fill text-accent-300" aria-hidden="true"></i>
                    {t}
                  </li>
                ))}
              </ul>
            </div>

            <div className="p-7 md:p-10 lg:p-12 bg-primary-600/40 border-t lg:border-t-0 lg:border-l border-background-50/15">
              <form
                id="subscribe-newsletter-form"
                data-readdy-form
                action="https://readdy.ai/api/form/dahc013oh653ivfvnqvg"
                method="POST"
                onSubmit={onSubmit}
                className="flex flex-col gap-3"
              >
                <label htmlFor="subscribe-email" className="text-sm font-medium text-background-50">
                  你的邮箱
                </label>
                <input
                  id="subscribe-email"
                  type="email"
                  name="email"
                  required
                  placeholder="you@example.com"
                  autoComplete="email"
                  className="w-full px-4 py-3 rounded-md bg-background-50 text-foreground-950 text-sm placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-accent-400"
                />
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="chigua-form-trap"
                />
                <label htmlFor="subscribe-interest" className="text-sm font-medium text-background-50 mt-1">
                  最想关注的档期
                </label>
                <select
                  id="subscribe-interest"
                  name="interest"
                  defaultValue="today"
                  className="w-full px-4 py-3 rounded-md bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-accent-400"
                >
                  <option value="today">今日上新</option>
                  <option value="weekly">本周推荐</option>
                  <option value="weekend">周末限定</option>
                  <option value="holiday">假日特辑</option>
                  <option value="hot">近期热点</option>
                </select>
                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="mt-2 w-full px-6 py-3.5 rounded-md bg-foreground-950 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-foreground-900 disabled:opacity-60 transition-colors"
                >
                  {status === 'submitting' ? '提交中…' : '订阅更新提醒'}
                </button>

                {status === 'success' && (
                  <p role="status" className="mt-1 flex items-start gap-2 text-sm text-background-50">
                    <i className="ri-checkbox-circle-fill text-accent-300 mt-0.5" aria-hidden="true"></i>
                    {message}
                  </p>
                )}
                {status === 'error' && (
                  <p role="alert" className="mt-1 flex items-start gap-2 text-sm text-background-50">
                    <i className="ri-error-warning-fill text-accent-300 mt-0.5" aria-hidden="true"></i>
                    {message}
                  </p>
                )}
                <p className="mt-1 text-xs text-primary-50/80">
                  提交即表示同意我们使用该邮箱发送档期提醒，我们不会对外分享你的邮箱。
                </p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}